import { CategoryIndex, EventResultsSection, SearchResultsSection } from '@components/display/search';
import { SkuSearchResults } from '@components/layout/search/SkuSearchResults';
import {
    CMS_DOCUMENTS_MEILISEARCH_HOST,
    CMS_DOCUMENTS_MEILISEARCH_KEY,
    eventSearchCategory,
    getCmsSearchCategories,
    newsSearchCategory,
    onlineTrainingSearchCategory,
    otherProfessionalSearchCategories,
    PROFESSIONAL_MEILISEARCH_HOST,
    PROFESSIONAL_MEILISEARCH_KEY,
} from '@constants/search';
import { useSearchContext } from '@contexts/SearchContext';
import { useAuth } from '@hooks/useAuth';
import { instantMeiliSearch } from '@meilisearch/instant-meilisearch';
import MemoryIcon from '@mui/icons-material/Memory';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import { useGetProductBySkuQuery } from '@services/products';
import { useMemo } from 'react';
import { InstantSearch } from 'react-instantsearch';
import { useIntl } from 'react-intl';

const professionalSearchClient =
    PROFESSIONAL_MEILISEARCH_HOST && PROFESSIONAL_MEILISEARCH_KEY
        ? instantMeiliSearch(PROFESSIONAL_MEILISEARCH_HOST, PROFESSIONAL_MEILISEARCH_KEY).searchClient
        : null;

const cmsDocumentsSearchClient =
    CMS_DOCUMENTS_MEILISEARCH_HOST && CMS_DOCUMENTS_MEILISEARCH_KEY
        ? instantMeiliSearch(CMS_DOCUMENTS_MEILISEARCH_HOST, CMS_DOCUMENTS_MEILISEARCH_KEY).searchClient
        : null;

export const SearchContent = ({ scope }: { scope?: string }) => {
    const { isLoggedIn } = useAuth();
    const intl = useIntl();
    const { search } = useSearchContext();
    const cmsSearchCategories = useMemo(() => getCmsSearchCategories(intl.locale), [intl.locale]);
    const { data: productData, isFetching: isProductBySkuLoading } = useGetProductBySkuQuery(search, {
        skip: /\s/.test(search),
    });

    const hasProfessionalClient = Boolean(professionalSearchClient && eventSearchCategory?.index);
    const hasCmsClient = Boolean(cmsDocumentsSearchClient && cmsSearchCategories?.[0].index);

    const professionalCategories = useMemo(
        () =>
            [
                newsSearchCategory,
                onlineTrainingSearchCategory,
                eventSearchCategory,
                ...otherProfessionalSearchCategories,
            ]
                .filter((category) => !scope || scope === (category.scope ?? category.index))
                .sort((a, b) => a.weight - b.weight),
        [scope]
    );
    const cmsCategories = useMemo(
        () =>
            cmsSearchCategories
                .filter((category) => !scope || scope === (category.scope ?? category.index))
                .sort((a, b) => a.weight - b.weight),
        [cmsSearchCategories, scope]
    );

    if (!hasProfessionalClient && !hasCmsClient) {
        const categories = [
            ...[
                newsSearchCategory,
                onlineTrainingSearchCategory,
                eventSearchCategory,
                ...otherProfessionalSearchCategories,
            ].map(({ index, name, icon, weight, scope: categoryScope }) => ({
                index,
                name,
                icon,
                weight,
                scope: categoryScope,
            })),
            ...cmsSearchCategories.map(({ index, name, icon, weight, scope: categoryScope }) => ({
                index,
                name,
                icon,
                weight,
                scope: categoryScope,
            })),
        ]
            .filter((category) => !scope || scope === (category.scope ?? category.index))
            .sort((a, b) => a.weight - b.weight);

        return (
            <>
                {categories.map((category) => (
                    <SearchResultsSection
                        key={category.index}
                        Icon={category.icon}
                        name={intl.formatMessage(category.name)}
                        results={[]}
                        search={search}
                    />
                ))}
            </>
        );
    }

    if (!isLoggedIn) {
        if (hasProfessionalClient && professionalSearchClient) {
            return (
                <InstantSearch searchClient={professionalSearchClient} indexName={newsSearchCategory.index}>
                    <CategoryIndex key="news" category={newsSearchCategory} search={search} />
                    {otherProfessionalSearchCategories
                        .filter((category) => category.index === 'firmware')
                        .map((category) => (
                            <CategoryIndex key={category.index} category={category} search={search} />
                        ))}
                </InstantSearch>
            );
        }

        return (
            <>
                <SearchResultsSection
                    Icon={NewspaperIcon}
                    name={intl.formatMessage({
                        id: 'search.category.newsArticles',
                        defaultMessage: 'News Articles',
                    })}
                    results={[]}
                    search={search}
                />
                <SearchResultsSection
                    Icon={MemoryIcon}
                    name={intl.formatMessage({ id: 'search.category.firmware', defaultMessage: 'Firmware' })}
                    results={[]}
                    search={search}
                />
            </>
        );
    }

    if (productData) {
        return <SkuSearchResults productData={productData} loading={isProductBySkuLoading} />;
    }

    return (
        <>
            {professionalSearchClient && (
                <InstantSearch searchClient={professionalSearchClient} indexName={newsSearchCategory.index}>
                    {professionalCategories.map((category) => (
                        <div key={category.index} style={{ order: category.weight }}>
                            {category.index === eventSearchCategory.index ? (
                                <CategoryIndex
                                    category={category}
                                    search={search}
                                    RenderComponent={EventResultsSection}
                                    initialHitsPerPage={4}
                                />
                            ) : (
                                <CategoryIndex category={category} search={search} />
                            )}
                        </div>
                    ))}
                </InstantSearch>
            )}
            {cmsDocumentsSearchClient && (
                <InstantSearch
                    searchClient={cmsDocumentsSearchClient}
                    indexName={cmsSearchCategories[0]?.index ?? 'documents'}
                >
                    {cmsCategories.map((category) => (
                        <div key={category.index} style={{ order: category.weight }}>
                            <CategoryIndex category={category} search={search} />
                        </div>
                    ))}
                </InstantSearch>
            )}
        </>
    );
};
